import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { CalendarCheck2, ChevronDown, Clock3, ShieldCheck } from 'lucide-react';
import { DnaHelix } from '@/components/animations/DnaHelix';
import { Button } from '@/components/ui/Button';
import { useReducedMotion } from '@/hooks/useReducedMotion';
import { useBookingCallToAction } from '@/hooks/useBookingCallToAction';

export const Hero: React.FC = () => {
  const reducedMotion = useReducedMotion();
  const { directBookingEnabled, triggerBooking } = useBookingCallToAction(import.meta.env.VITE_BOOKING_URL);
  const particles = useMemo(
    () =>
      Array.from({ length: 14 }, () => ({
        left: `${Math.random() * 100}%`,
        top: `${Math.random() * 100}%`,
        duration: 4 + Math.random() * 4,
        delay: Math.random() * 2,
      })),
    []
  );

  const scrollToNext = () => {
    const element = document.querySelector('#profile');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };
  const bookingLabel = directBookingEnabled ? 'Book 20-Min Intro Call' : 'Request 20-Min Intro Call';

  return (
    <section className="hero-section relative min-h-screen min-h-[100svh] w-full flex items-center overflow-hidden bg-bg-primary">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-bg-primary via-bg-secondary to-bg-primary opacity-50" />
      
      {/* Floating particles */}
      <div className="absolute inset-0 pointer-events-none">
        {particles.map((particle, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-apple-blue/30 rounded-full"
            style={{
              left: particle.left,
              top: particle.top,
            }}
            animate={reducedMotion ? {} : {
              y: [0, -30, 0],
              opacity: [0.2, 0.6, 0.2],
            }}
            transition={{
              duration: particle.duration,
              repeat: Infinity,
              delay: particle.delay,
              ease: 'easeInOut',
            }}
          />
        ))}
      </div>

      <div className="hero-content relative w-full px-6 md:px-12 lg:px-16 xl:px-24 pt-20 pb-24 md:pb-20 lg:pb-0">
        <div className="max-w-wrapper mx-auto">
          <div className="grid lg:grid-cols-2 gap-8 md:gap-12 lg:gap-8 items-center min-h-[calc(100svh-5rem)] md:min-h-[calc(100vh-6rem)]">
            {/* Left Content */}
            <div className="order-2 lg:order-1 text-center lg:text-left">
              {/* Overline */}
              <motion.div
                initial={reducedMotion ? {} : { opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="mb-6"
              >
                <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-apple-blue/10 border border-apple-blue/20 text-apple-blue text-caption font-medium uppercase tracking-wider">
                  <span className="w-2 h-2 rounded-full bg-apple-blue animate-pulse" />
                  Available for Advisory
                </span>
              </motion.div>

              {/* Name */}
              <motion.h1
                initial={reducedMotion ? {} : { opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="text-display-m md:text-display-m lg:text-display-l font-bold tracking-tight mb-4"
              >
                <span className="gradient-text">Christer T.</span>
                <br />
                <span className="text-text-primary">Larsen</span>
                <span className="text-apple-blue">, MD</span>
              </motion.h1>

              {/* Title */}
              <motion.p
                initial={reducedMotion ? {} : { opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="text-h4 text-text-secondary font-medium mb-6"
              >
                Clinical Strategy for Executives Who Can't Afford the Wrong Call
              </motion.p>

              {/* Description */}
              <motion.p
                initial={reducedMotion ? {} : { opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.5 }}
                className="text-body-l text-text-tertiary max-w-xl mx-auto lg:mx-0 mb-8"
              >
                I work with executive teams building healthcare products and risk programs —
                translating what the medicine actually requires into decisions that engineering
                and operations can execute.
              </motion.p>

              {/* CTA Buttons */}
              <motion.div
                initial={reducedMotion ? {} : { opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
                className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start"
              >
                <Button
                  variant="primary"
                  size="lg"
                  onClick={triggerBooking}
                  icon={<CalendarCheck2 className="w-4 h-4" />}
                >
                  {bookingLabel}
                </Button>
                <Button variant="secondary" size="lg" href="#services">
                  View Advisory Services
                </Button>
              </motion.div>

              <motion.p
                initial={reducedMotion ? {} : { opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.7 }}
                className="mt-4 text-body-s text-text-tertiary"
              >
                Typical response within 48 hours. Global engagements supported.
              </motion.p>

              {/* Quick stats */}
              <motion.div
                initial={reducedMotion ? {} : { opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.8 }}
                className="flex flex-wrap justify-center lg:justify-start gap-8 mt-8 md:mt-12 pt-6 md:pt-8 border-t border-border-subtle"
              >
                <div className="text-center">
                  <div className="text-h3 font-bold text-text-primary">10+</div>
                  <div className="text-caption text-text-tertiary uppercase tracking-wider">Years Experience</div>
                </div>
                <div className="text-center">
                  <div className="text-h3 font-bold text-text-primary">MD</div>
                  <div className="text-caption text-text-tertiary uppercase tracking-wider">Family Medicine</div>
                </div>
                <div className="text-center">
                  <div className="text-h3 font-bold text-text-primary">Global</div>
                  <div className="text-caption text-text-tertiary uppercase tracking-wider">Availability</div>
                </div>
              </motion.div>

              <motion.div
                initial={reducedMotion ? {} : { opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.6, delay: 0.9 }}
                className="mt-6 flex flex-wrap justify-center lg:justify-start gap-2"
              >
                <span className="inline-flex items-center gap-1.5 rounded-full border border-border-subtle bg-bg-tertiary px-3 py-1.5 text-caption text-text-secondary">
                  <ShieldCheck className="w-3.5 h-3.5 text-apple-blue" />
                  Licensed Physician, Norway
                </span>
                <span className="inline-flex items-center gap-1.5 rounded-full border border-border-subtle bg-bg-tertiary px-3 py-1.5 text-caption text-text-secondary">
                  <CalendarCheck2 className="w-3.5 h-3.5 text-apple-blue" />
                  Board-Level Advisory
                </span>
                <span className="inline-flex items-center gap-1.5 rounded-full border border-border-subtle bg-bg-tertiary px-3 py-1.5 text-caption text-text-secondary">
                  <Clock3 className="w-3.5 h-3.5 text-apple-blue" />
                  48h Response Target
                </span>
              </motion.div>
            </div>

            {/* Right - DNA Helix */}
            <motion.div
              initial={reducedMotion ? {} : { opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="order-1 lg:order-2 relative h-[240px] sm:h-[300px] md:h-[400px] lg:h-[500px]"
            >
              <DnaHelix className="w-full h-full" />
              
              {/* Decorative elements */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-apple-blue/5 rounded-full blur-3xl" />
            </motion.div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <motion.button
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2 }}
        onClick={scrollToNext}
        className="hero-scroll-indicator absolute bottom-4 md:bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-text-tertiary hover:text-text-secondary transition-colors cursor-pointer"
        aria-label="Scroll to next section"
      >
        <span className="text-caption uppercase tracking-wider">Scroll</span>
        <motion.div
          animate={reducedMotion ? { y: 0 } : { y: [0, 8, 0] }}
          transition={reducedMotion ? { duration: 0 } : { duration: 1.5, repeat: Infinity }}
        >
          <ChevronDown className="w-5 h-5" />
        </motion.div>
      </motion.button>
    </section>
  );
};

export default Hero;
